import Header from "@/components/Header";
import SiteFooter from "@/components/SiteFooter";
import PageHero from "@/components/PageHero";
import BookCTA from "@/components/BookCTA";
import { Heart } from "lucide-react";
import { usePageMeta } from "@/lib/seo";

const TEAM_PHOTO_URL =
  "https://customer-assets-jt897jd0.emergentagent.net/wingman/9895e16b-8404-4820-b934-3100c7eb0dd7/attachments/6f0e5c019df14d1aa02fe3725a16224f_image.bin";

const TEAM = [
  { name: "Terry Waites", role: "Owner & Founder", note: "Meet Terry Waites, the founder of Waites Clearances! Terry is a proud family man who has spent the last 5 years building a family-run business built on hard work, honesty and great customer service. As an environmental enthusiast, Terry is passionate about giving unwanted items a new lease of life, reducing waste and making sure good-quality items find a new home rather than going to landfill. For Terry, Waites Clearances is more than just a business – it's about family, community and making a positive difference!" },
  { name: "Kieran Waites", role: "Son", note: "Meet Kieran, Terry's son and a key part of the Waites Clearances family! Having helped build the business alongside his dad, Kieran brings plenty of hard work, dedication and a natural ability to connect with people. Whether he's helping customers, getting stuck into a clearance or making sure the job gets done properly, for him it's all about teamwork, treating people right and helping the family business continue to grow!" },
  { name: "Tiffany Ellerston", role: "Daughter-in-law", note: "Meet Tiff, our daughter-in-law and the woman keeping Waites Clearances organised behind the scenes! From managing the diary and planning the jobs to keeping on top of the day-to-day running of the business, Tiff helps make sure everything runs smoothly. While the lads are out on the clearances, she's busy coordinating schedules, organising the team and keeping everything on track but also keen to get involved. A key part of the family business, Tiff is proof that behind every hardworking team is someone making sure all the pieces fit together!" },
  { name: "Ben Brooker", role: "Son-in-law", note: "Meet Ben, our son-in-law and one of the founding forces behind Waites Clearances! Ben has helped build the foundations of the business from the ground up and is always ready to roll up his sleeves and get stuck in. Known as the muscles of the team, he's our go-to guy for the heavy lifting and making light work of the toughest jobs! With plenty of hard work, dedication and a can-do attitude, Ben plays a huge part in keeping the Waites family business moving forward." },
];

const initials = (name) => name.split(" ").map((p) => p[0]).join("");
const slug = (name) => name.toLowerCase().replace(/[^a-z]+/g, "-");

function TeamCard({ name, role, note, index }) {
  return (
    <div
      data-testid={`team-member-${slug(name)}`}
      className="group rounded-3xl border border-slate-200 bg-white p-7 hover:border-[#FFB870] hover:shadow-lg hover:-translate-y-0.5 transition-[transform,box-shadow,border-color] animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-both"
      style={{ animationDelay: `${index * 90}ms` }}
    >
      <div className="h-16 w-16 rounded-2xl bg-[#FF7F00] text-white flex items-center justify-center font-display text-xl font-extrabold shadow-md shadow-orange-200 group-hover:scale-105 transition-transform">
        {initials(name)}
      </div>
      <h3 className="mt-5 font-display text-xl font-extrabold text-slate-900">{name}</h3>
      <p className="mt-1 text-sm font-semibold text-[#FF7F00]" data-testid={`team-role-${slug(name)}`}>{role}</p>
      <p className="mt-3 text-sm text-slate-600 leading-relaxed">{note}</p>
    </div>
  );
}

export default function Team() {
  usePageMeta(
    "Meet the Team | Waites Clearances Peterborough",
    "Meet the family behind Waites Clearances — Terry, Kieran, Tiffany and Ben. A friendly, family run house clearance team based in Peterborough."
  );
  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="page-team">
      <Header />

      <PageHero
        eyebrow="Meet the team"
        title="The Peterborough family behind Waites Clearances."
        description="We're a proper family business based in Peterborough. When you call us, it's one of us who answers — and it's the same friendly faces who turn up on the day to take care of your clearance."
      />

      <section className="py-20 sm:py-24 bg-white border-y border-slate-100" data-testid="team-grid-section">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <div className="grid md:grid-cols-2 gap-5">
            {TEAM.map((m, i) => (
              <TeamCard key={m.name} {...m} index={i} />
            ))}
          </div>

          <div className="mt-12 flex items-start gap-4 rounded-2xl bg-[#FFF3E6] border border-[#FFB870] p-6 max-w-3xl">
            <Heart className="h-6 w-6 text-[#FF7F00] shrink-0 mt-0.5" />
            <p className="text-slate-700 leading-relaxed">
              Family first, always. We look after every Peterborough home like it belongs to one of our own —
              with honest prices, careful hands and a friendly chat along the way.
            </p>
          </div>

          <figure className="mt-14" data-testid="team-group-photo">
            <div className="overflow-hidden rounded-3xl border border-[#FFB870] shadow-xl shadow-orange-100">
              <img
                src={TEAM_PHOTO_URL}
                alt="The Waites Clearances family team in Peterborough"
                className="w-full h-auto lg:h-[620px] object-cover object-[center_45%]"
                loading="lazy"
              />
            </div>
            <figcaption
              className="mt-4 text-center font-display text-base sm:text-lg font-semibold text-slate-700"
              data-testid="team-group-photo-caption"
            >
              <Heart className="inline h-4 w-4 mr-2 -mt-0.5 text-[#FF7F00]" fill="#FF7F00" />
              The Waites Clearances team, Peterborough.
            </figcaption>
          </figure>
        </div>
      </section>

      <BookCTA
        title="Say hello to the family."
        body="Send us the details and photos and one of us will get back to you with a free quote for your Peterborough clearance."
      />

      <SiteFooter />
    </div>
  );
}
